/**
 * WiseTableFilterList – parametr typu "list" dla WiseTable.
 * Wyświetla WiseSelect (trigger), po kliknięciu otwiera MUI Dialog
 * z wyszukiwarką, tabelą opcji i paginacją.
 */

import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Typography
} from '@mui/material';
import { Search, X } from 'react-bootstrap-icons';
import { Pagination } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';
import { useLoadData } from 'plugins/api/endpoints/useLoadData';
import { useAppTheme } from 'theme/useAppTheme';
import { IWiseTableParameter, IWiseTableQueryParams } from 'components/wise/types';

import styles from 'theme/components/wise/WiseTable/components/WiseTableFilterList/WiseTableFilterList.module.scss';

interface IWiseTableFilterListProps {
  parameter: IWiseTableParameter;
  queryParams: IWiseTableQueryParams;
  updateParameter: (fieldSymbol: string, value: unknown) => void;
}

const PAGE_SIZE = 10;

const WiseTableFilterList: FC<IWiseTableFilterListProps> = ({
  parameter,
  queryParams,
  updateParameter
}) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const fieldSymbol = parameter.field_symbol || '';
  const currentValue = queryParams[fieldSymbol] ?? null;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);

  const apiUrl = parameter.dictionary?.values_get_u_i_api || '';

  // Słownik dla WiseSelect (trigger) - ładowany na mount
  const {
    data: dictionaryData,
    refetch: refetchDictionary
  } = useLoadData(apiUrl, { page: 1, limit: 999 }, { enabled: false });

  useEffect(() => {
    if (apiUrl) {
      refetchDictionary();
    }
  }, [apiUrl]);

  // Dane dla dialogu - z paginacją i wyszukiwarką
  const dialogQueryParams = useMemo(
    () => ({ page, limit: PAGE_SIZE, searchKeyword }),
    [page, searchKeyword]
  );

  const {
    data: dialogData,
    refetch: refetchDialog,
    isLoading
  } = useLoadData(apiUrl, dialogQueryParams, { enabled: false });

  useEffect(() => {
    if (dialogOpen && apiUrl) {
      refetchDialog();
    }
  }, [dialogOpen, apiUrl, dialogQueryParams]);

  const handleOpen = useCallback(() => {
    setSearchKeyword('');
    setPage(1);
    setDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleSelect = useCallback(
    (value: unknown) => {
      updateParameter(fieldSymbol, value);
      setDialogOpen(false);
    },
    [fieldSymbol, updateParameter]
  );

  const handleClear = useCallback(() => {
    updateParameter(fieldSymbol, null);
  }, [fieldSymbol, updateParameter]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchKeyword(e.target.value);
      setPage(1);
    },
    []
  );

  const handleSearchClear = useCallback(() => {
    setSearchKeyword('');
    setPage(1);
  }, []);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        handleSearchClear();
      }
    },
    [handleSearchClear]
  );

  const selectOptions = useMemo(
    () =>
      (dictionaryData?.items || parameter.dictionary?.values || []).map(
        (item: { value: string | number; text: string }) => ({
          value: item.value,
          label: item.text,
          item
        })
      ),
    [dictionaryData, parameter.dictionary?.values]
  );

  const items = dialogData?.items || [];
  const totalPages = dialogData?.total_pages || 1;

  const skeletonRows = Array.from({ length: PAGE_SIZE }, (_, i) => i);

  return (
    <>
      <WiseSelect
        styleGroup="wise-table"
        placeholder={parameter.label}
        value={currentValue}
        options={selectOptions}
        displayVariant="modal"
        clearable
        onOpenChange={(open) => {
          if (open) handleOpen();
        }}
        onChange={(item) => {
          if (!item) handleClear();
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1
          }}
        >
          <Typography variant="h6" component="span" fontWeight={600}>
            {parameter.label}
          </Typography>
          <IconButton onClick={handleClose} size="small" aria-label={t('Zamknij')}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <div className={styles.dialogContent}>
            <div className={styles.searchRow}>
              <TextField
                className={styles.searchInput}
                size="small"
                placeholder={`${t('Szukaj')}...`}
                value={searchKeyword}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                  endAdornment: searchKeyword ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearchClear} size="small" edge="end">
                        <X size={18} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    '& fieldset': {
                      borderColor: theme.palette.app.textMutedAlpha
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.app.textMutedAlpha
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.app.secondaryLight
                    }
                  }
                }}
              />
              {searchKeyword && (
                <Button
                  className={styles.clearButton}
                  size="small"
                  color="secondary"
                  onClick={handleSearchClear}
                >
                  {t('Wyczyść')}
                </Button>
              )}
            </div>

            <div className={styles.tableWrapper}>
              {isLoading ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableBody>
                      {skeletonRows.map((i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton variant="text" height={28} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : items.length === 0 ? (
                <div className={styles.emptyState}>
                  {t('Brak wyników')}
                </div>
              ) : (
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            color: theme.palette.text.secondary
                          }}
                        >
                          {t('Nazwa')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item: { value: unknown; text: string; id?: unknown }) => {
                        const isSelected = item.value === currentValue;
                        return (
                          <TableRow
                            key={String(item.id ?? item.value)}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleSelect(item.value)}
                            selected={isSelected}
                          >
                            <TableCell sx={{ py: 0.5, px: 1 }}>
                              <button
                                type="button"
                                className={`${styles.rowButton} ${isSelected ? styles.selected : ''}`}
                              >
                                {item.text}
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>

            {totalPages > 1 && (
              <div className={styles.paginationRow}>
                <Pagination
                  pagesCount={totalPages}
                  page={page}
                  onChange={(newPage) => {
                    if (newPage !== page) setPage(newPage);
                  }}
                />
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button color="secondary" onClick={handleClose}>
            {t('Anuluj')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WiseTableFilterList;
